import type { GenericObject } from '@makemydeal/dr-platform-types';
import { FINANCE, BalloonAvailableMonths } from '@makemydeal/dr-platform-types';
import {
    UPDATE_BALLOON_PAYMENT,
    UPDATE_BALLOON_PAYMENT_GRID_DATA,
    UPDATE_BALLOON_PAYMENT_GRID_AXIS_VALUES,
    LOADING_BALLOON_PAYMENT_GRID,
    UPDATED_BALLOON_PAYMENT_GRID_TOGGLE,
    RESET_BALLOON_PAYMENT_GRID_DATA,
    REVERT_BALLOON_DEAL_TO_BASE_FINANCE
} from '../types/actionTypes';
import type { UpdateBalloonPaymentGridDataPayload, BalloonPaymentGridAxisValues } from '../types/balloonTypes';

/**
 * Default balloon term options (in months)
 * These represent common balloon payment term lengths
 */
export const balloonTermList = [
    BalloonAvailableMonths.MONTH_36,
    BalloonAvailableMonths.MONTH_48,
    BalloonAvailableMonths.MONTH_60,
    BalloonAvailableMonths.MONTH_72
];

/**
 * Updates the balloon payment term
 * @param newTermValue - The new balloon term in months
 * @param options - Additional options for the action
 */
export const updateBalloonPaymentTerm = (
    newTermValue: number,
    options?: {
        meta?: GenericObject<unknown>;
        requestPayment?: boolean;
    }
) => {
    return {
        type: UPDATE_BALLOON_PAYMENT as typeof UPDATE_BALLOON_PAYMENT,
        payload: { balloonTerm: newTermValue },
        meta: {
            ...options?.meta,
            originalAction: { meta: { middleware: { offerType: FINANCE } } },
            middleware: {
                payment: options?.requestPayment ?? true
            }
        }
    } as const;
};

/**
 * Updates the balloon payment amount
 * @param newAmountValue - The new balloon amount
 * @param options - Additional options for the action
 */
export const updateBalloonPaymentAmount = (
    newAmountValue: number,
    options?: {
        meta?: GenericObject<unknown>;
        requestPayment?: boolean;
    }
) => {
    return {
        type: UPDATE_BALLOON_PAYMENT as typeof UPDATE_BALLOON_PAYMENT,
        payload: { balloonAmount: newAmountValue },
        meta: {
            ...options?.meta,
            originalAction: { meta: { middleware: { offerType: FINANCE } } },
            middleware: {
                payment: options?.requestPayment ?? true
            }
        }
    } as const;
};

/**
 * Updates the balloon payment type (Term or Term-1)
 * @param newTypeValue - The new balloon type
 * @param options - Additional options for the action
 */
export const updateBalloonPaymentType = (
    newTypeValue: 'Term' | 'Term-1',
    options?: {
        meta?: GenericObject<unknown>;
        requestPayment?: boolean;
    }
) => {
    return {
        type: UPDATE_BALLOON_PAYMENT as typeof UPDATE_BALLOON_PAYMENT,
        payload: { balloonType: newTypeValue },
        meta: {
            ...options?.meta,
            originalAction: { meta: { middleware: { offerType: FINANCE } } },
            middleware: {
                payment: options?.requestPayment ?? true
            }
        }
    } as const;
};

/**
 * Updates balloon payment grid data
 * @param payload - The grid data payload
 * @param options - Additional options
 */
export const updateBalloonPaymentGridData = (
    payload: UpdateBalloonPaymentGridDataPayload,
    options?: {
        meta?: GenericObject<unknown>;
    }
) => {
    return {
        type: UPDATE_BALLOON_PAYMENT_GRID_DATA as typeof UPDATE_BALLOON_PAYMENT_GRID_DATA,
        payload,
        meta: {
            ...options?.meta
        }
    } as const;
};

/**
 * Updates balloon payment grid axis values (terms, down payments)
 * @param payload - The axis values
 */
export const updateBalloonPaymentGridAxisValues = (payload: BalloonPaymentGridAxisValues) => {
    return {
        type: UPDATE_BALLOON_PAYMENT_GRID_AXIS_VALUES as typeof UPDATE_BALLOON_PAYMENT_GRID_AXIS_VALUES,
        payload
    } as const;
};

/**
 * Initializes loading state for balloon payment grid
 * @param payload - Terms and down payments for the grid
 */
export const loadingBalloonPaymentGrid = (payload: { balloonTerms: number[]; balloonDownPayments: number[] }) => {
    return {
        type: LOADING_BALLOON_PAYMENT_GRID as typeof LOADING_BALLOON_PAYMENT_GRID,
        payload
    } as const;
};

/**
 * Toggles the balloon payment grid visibility
 * @param isVisible - Whether the grid should be visible
 */
export const updateBalloonPaymentGridToggle = (isVisible: boolean) => {
    return {
        type: UPDATED_BALLOON_PAYMENT_GRID_TOGGLE as typeof UPDATED_BALLOON_PAYMENT_GRID_TOGGLE,
        payload: isVisible
    } as const;
};

/**
 * Resets balloon payment grid data to initial state
 */
export const resetBalloonPaymentGridData = () => {
    return {
        type: RESET_BALLOON_PAYMENT_GRID_DATA as typeof RESET_BALLOON_PAYMENT_GRID_DATA
    } as const;
};

/**
 * Reverts a balloon deal back to base finance
 * Used when user wants to switch from balloon to standard finance
 */
export const revertBalloonDealToBaseFinance = (options?: { meta?: GenericObject<unknown>; requestPayment?: boolean }) => {
    return {
        type: REVERT_BALLOON_DEAL_TO_BASE_FINANCE as typeof REVERT_BALLOON_DEAL_TO_BASE_FINANCE,
        meta: {
            ...options?.meta,
            originalAction: { meta: { middleware: { offerType: FINANCE } } },
            middleware: {
                payment: options?.requestPayment ?? true,
                offerType: FINANCE
            }
        }
    } as const;
};
